// A small library of "Quiet Reads" — short, modern, gender-neutral essays
// that live on the Mind → A Quiet Read tab. Each article uses the same
// shape as the Stoicism guide so the reader component can render any of
// them without change.
//
// Shape:
//   {
//     slug, title, subtitle, minutes, accent (lucide icon name),
//     intro, sections: [
//       { heading, body?, bullets?, subsections?, closing?, quotes? }
//     ],
//     reflection_prompts: [ ... ]
//   }
//
// The Stoicism article lives in stoicContent.js and is re-exported here so
// existing imports keep working.

import { STOIC_ARTICLE } from "@/lib/stoicContent";

const LOVE_YOU_BECOME = {
  slug: "the-love-you-become",
  title: "The Love You Become",
  subtitle:
    "What if becoming was never about earning your worth—but finally recognizing it?",
  minutes: 8,
  accent: "Sun",
  featured: true,
  feature_label: "Foundational Read",
  hero_image:
    "https://images.unsplash.com/photo-1490730141103-6cac27aaab94?w=1800&q=80&auto=format&fit=crop",
  card_preview:
    "Self-love changes what you believe you deserve, what you allow, what you choose, and the life you participate in creating.",
  intro:
    "Perhaps the deepest part of becoming isn't becoming someone new. Perhaps it's learning to love the person who has been there all along. We spend so much of our lives becoming for something. More successful. More attractive. More accomplished. More healed. More desirable. More worthy — as though somewhere in the distance there is a version of us who will finally have earned the right to feel whole. But what if becoming was never about earning your worth? What if it was about finally recognizing it?",
  sections: [
    {
      heading: "Self-love changes the starting point",
      body:
        "There is a profound difference between creating a life because you believe something is missing from you and creating one because you believe your life is worthy of being beautiful. One says: \"When I have enough, I'll feel valuable.\" The other says: \"Because I know my value, I choose what reflects it.\" That shift changes more than the way you feel about yourself. It changes the way you live.",
      subsections: [
        {
          title: "You stop asking",
          body:
            "\"Will they choose me?\" And begin asking: \"Does this relationship honor me?\"",
        },
        {
          title: "You stop asking",
          body:
            "\"Am I good enough for this opportunity?\" And begin asking: \"Does this opportunity belong in the life I'm creating?\"",
        },
        {
          title: "You stop asking",
          body:
            "\"How do I make myself more acceptable?\" And begin asking: \"Where am I no longer willing to abandon myself for acceptance?\"",
        },
      ],
      closing:
        "Self-love quietly changes the questions. And when the questions change, your choices often do too.",
      pull_quote: "Because I know my value, I choose what reflects it.",
    },
    {
      heading: "Worth is not the same as achievement",
      body:
        "Your worth is not a reward. It isn't something another person gives you when they love you correctly. It isn't created by your appearance. It doesn't increase with your income. It isn't proven by how much you accomplish, how useful you are, how much you give, or how many people approve of you. Those things may influence how you feel about yourself. But they do not determine your value.",
      closing:
        "There is a difference between building a life you're proud of and believing you must build an impressive life before you're allowed to be proud of yourself. Self-love allows both truths to exist. You can love who you are and still want to grow. You can accept yourself and still change. You can know your value and still become. In fact, that may be where the most meaningful becoming begins.",
      pull_quote: "I am worthy now. And I still desire more for my life.",
    },
    {
      heading: "Your sense of worth teaches you what feels normal",
      body:
        "What we believe we deserve can quietly influence what we tolerate, pursue, decline, return to, and walk away from. When your relationship with yourself changes, your standards often change with it. You may begin noticing:",
      bullets: [
        "Where you've been accepting inconsistency because you wanted connection.",
        "Where you've been overgiving because being needed made you feel valuable.",
        "Where you've been shrinking because belonging felt safer than being fully seen.",
        "Where you've been chasing something because obtaining it seemed like proof that you were enough.",
      ],
      closing:
        "None of this needs to become another reason to judge yourself. It can simply become something you see. Because once you see it, you have another choice.",
    },
    {
      heading: "This is where alignment begins",
      body:
        "Alignment doesn't have to mean that every thought is positive or that life suddenly gives you everything you desire. It can be much simpler — and more powerful — than that. What I believe about myself. What I say I want. What I choose. What I allow. What I practice. Where I place my energy. Begin moving in the same direction.",
      closing:
        "Imagine saying: \"I desire a peaceful life.\" Then continuing to participate in relationships, habits, environments, or commitments that repeatedly cost you your peace. There is a gap between the desire and the way you're living. Self-love begins closing that gap. Not perfectly. Intentionally. You begin making choices that are increasingly congruent with the life you say you want. That is alignment.",
    },
    {
      heading: "And alignment changes what becomes possible",
      body:
        "There is something beautiful about imagining the reality you want to experience — love, abundance, freedom, vitality, purpose, connection, adventure, peace, whatever your vision contains. But creating that reality isn't only about imagining the destination. It's also about becoming increasingly available for the choices that lead toward it. When you value yourself, you may:",
      bullets: [
        "Recognize opportunities you once would have dismissed.",
        "Leave situations you once believed you had to tolerate.",
        "Communicate differently. Take the risk. Set the boundary. Ask for more.",
        "Rest without earning it first.",
        "Allow yourself to be seen. Receive without immediately feeling obligated to repay.",
        "Say yes because you genuinely desire something. Say no because you genuinely don't.",
      ],
      closing:
        "Self-love changes the relationship between desire and permission. You stop merely asking: \"Could I have that life?\" And begin considering: \"Am I willing to live like my desires matter?\"",
      pull_quote:
        "Self-love changes the relationship between desire and permission.",
    },
    {
      heading: "Maybe manifestation begins here",
      body:
        "Not with controlling every outcome. Not with pretending difficult feelings don't exist. Not with believing that every experience in your life was created by your thoughts. But with becoming deeply honest about the reality you desire — and noticing whether your relationship with yourself supports your movement toward it.",
      closing:
        "There will always be circumstances you cannot control. Other people's choices. Timing. Loss. Chance. Opportunity. Change. Life itself. Self-love doesn't promise control over those things. It changes how you meet them. It allows you to participate in creating your life from worth rather than proving, intention rather than desperation, and choice rather than self-abandonment. And perhaps that is one of the deepest forms of alignment available to us.",
      pull_quote:
        "Knowing your worth changes the reality you are willing to participate in creating.",
    },
    {
      heading: "The ultimate becoming",
      body:
        "Maybe the person you're becoming isn't someone you finally learn to admire someday. Maybe becoming is the process through which you stop withholding love from yourself until you've arrived.",
      bullets: [
        "You learn yourself.",
        "You honor yourself.",
        "You forgive yourself.",
        "You protect what matters to you.",
        "You listen when something within you says yes. You listen when something within you says no.",
        "You care for your body because it carries you.",
        "You tend to your mind because you live there.",
        "You nurture your soul because there are parts of you that cannot be measured by productivity.",
      ],
      closing:
        "And slowly, the life you're creating begins to reflect the relationship you're building with yourself. Not because self-love guarantees a particular reality. But because knowing your worth changes the reality you are willing to participate in creating.",
    },
  ],
  reflection_prompts: [
    "Where in my life am I already choosing from self-love?",
    "Where am I still trying to earn something I wish I simply knew I deserved?",
    "If I deeply trusted my own value, what would I stop accepting? What would I allow myself to receive?",
    "What would I choose differently?",
  ],
  emphasized_prompt:
    "If I already believed I was worthy of the life I desire, how would I begin meeting my life today?",
  epilogue: [
    "You do not have to become worthy. Your becoming can be the journey of remembering that you already have value — and learning to build a life that reflects it.",
    "The goal isn't simply to age. It's to become.",
    "Perhaps the most meaningful person you will ever become is someone who knows how to love themselves.",
  ],
};

const SOLITUDE = {
  slug: "solitude",
  title: "Solitude",
  subtitle: "The space where you meet yourself.",
  minutes: 9,
  accent: "Moon",
  featured: true,
  feature_label: "Foundational Read",
  hero_image:
    "https://images.unsplash.com/photo-1523712999610-f77fbcfc3843?w=1800&q=80&auto=format&fit=crop",
  card_preview:
    "Being alone and being lonely are not the same thing. Solitude can be the discovery that something has been waiting for you.",
  intro:
    "Being alone and being lonely are not the same thing. Loneliness is the feeling that something is missing. Solitude can be the discovery that something has been waiting for you. You. There are seasons of life when the noise becomes quieter. Relationships change. Friendships shift. Children leave home. Careers evolve. A chapter closes. Or perhaps you simply begin craving more space than you once did. At first, that space can feel unfamiliar. We live in a world that often treats being surrounded by people as evidence of a full life. An empty calendar can look like something that needs to be filled. A quiet evening can feel like something we should escape. But there is another way to experience being alone. Not as an absence. As an opening.",
  sections: [
    {
      heading: "Solitude creates space to hear yourself",
      body:
        "Much of life is lived in response to other people. What do they need? What do they expect? What will they think? Where do I need to be? Who needs something from me? Even the people we deeply love create a rhythm around us. We compromise. Adjust. Consider. Accommodate. Connect. These are beautiful parts of being human. But sometimes, somewhere beneath all of that relating, our own voice becomes difficult to hear.",
      closing:
        "Solitude turns down the volume. And in the quiet, different questions begin to emerge: What do I actually enjoy? What do I want when no one else is influencing the answer? What have I outgrown? What have I been too busy to notice? What feels meaningful to me now? There is a kind of self-knowledge that becomes possible when there is no one else in the room to perform for.",
      pull_quote:
        "Loneliness is the feeling that something is missing. Solitude can be the discovery that something has been waiting for you.",
    },
    {
      heading: "Alone does not mean lonely",
      body:
        "You can be surrounded by people and feel profoundly lonely. And you can sit alone on a porch, walk through the woods, cook dinner for yourself, travel somewhere new, or spend an evening reading and feel deeply connected to life. Because loneliness isn't simply a measurement of how many people are nearby. Connection has many forms.",
      bullets: [
        "Connection to yourself.",
        "Connection to nature.",
        "Connection to creativity.",
        "Connection to purpose.",
        "Connection to your body.",
        "Connection to something greater than yourself.",
        "Connection to the life unfolding around you.",
        "And, of course, connection to other people.",
      ],
      closing:
        "Solitude doesn't require rejecting human connection. Healthy solitude and meaningful connection can coexist. The goal isn't to convince yourself that you don't need anyone. We do need one another. The opportunity is learning that needing connection doesn't mean being afraid of your own company.",
    },
    {
      heading: "There are things the quiet can teach you",
      body:
        "When there is no distraction, you may begin noticing yourself differently.",
      bullets: [
        "What you reach for when you're uncomfortable.",
        "What thoughts appear when everything becomes quiet.",
        "What dreams keep returning.",
        "What you've been tolerating.",
        "What you've been avoiding.",
        "What gives you energy.",
        "What drains it.",
        "What you miss. What you don't miss at all.",
      ],
      closing:
        "Some discoveries will feel beautiful. Others may be uncomfortable. That's part of the opportunity. Growth doesn't always happen while we're adding something to our lives. Sometimes it happens when enough falls away for us to finally see what was underneath.",
      pull_quote:
        "Sometimes growth happens when enough falls away for us to finally see what was underneath.",
    },
    {
      heading: "Solitude can become a place of creation",
      body:
        "There is another gift hidden inside being alone: freedom to experiment. You can change your mind. Try something badly. Learn something new.",
      bullets: [
        "Dance in your kitchen.",
        "Take yourself somewhere you've always wanted to go.",
        "Start writing. Plant something. Build something. Rest.",
        "Rearrange your home.",
        "Spend an afternoon doing absolutely nothing productive.",
        "Discover music you've never heard.",
        "Take the long way home.",
        "Become curious about your own life again.",
      ],
      closing:
        "There is no audience required. No approval necessary. Solitude can become the place where you discover what genuinely makes you feel alive — not because someone else enjoys it, praises it, understands it, or joins you. Simply because you do.",
    },
    {
      heading: "And that changes relationships, too",
      body:
        "Learning to enjoy your own company doesn't make relationships less important. It can change why you choose them. When being alone no longer feels like something you must escape, connection becomes less about filling an emptiness and more about sharing a life that already belongs to you.",
      closing:
        "You can begin asking: \"Do I genuinely enjoy this connection?\" rather than: \"Is this better than being alone?\" You can allow people into your life because their presence adds something meaningful — not simply because their absence frightens you. That distinction can change friendships, love, boundaries, standards. And perhaps most importantly, your relationship with yourself.",
    },
    {
      heading: "Solitude is not always comfortable",
      body:
        "There may still be nights when you wish someone were beside you. Moments when you want someone to call. Experiences you wish you could immediately share. Missing connection doesn't mean you've failed at solitude.",
      closing:
        "You can love your own company and still desire companionship. You can be independent and still want intimacy. You can feel whole and still want someone to share your life with. These things do not contradict one another. Self-sufficiency was never supposed to mean never needing connection. The deeper freedom is knowing that your life does not have to stop while you wait for someone else to enter it.",
    },
    {
      heading: "There is a life happening here, too",
      body:
        "Your alone seasons are not waiting rooms. They are not the space between the real chapters of your life. This is your life. This morning. This quiet house. This walk. This meal. This idea. This sunset no one else saw. This version of you learning what it feels like to belong to yourself.",
      closing:
        "Don't rush through it simply because you imagine another chapter will be better. There may come a day when your life is louder again. More people. More responsibility. More companionship. More demands on your attention. And you may look back and realize that this quiet season gave you something you couldn't have received in quite the same way anywhere else: the opportunity to meet yourself without interruption.",
      pull_quote: "Your alone seasons are not waiting rooms.",
    },
    {
      heading: "The growth within solitude",
      body:
        "Solitude asks something different from you than loneliness does. Loneliness asks: \"Who is missing?\" Solitude invites you to ask: \"What is here?\" And maybe what is here is more than you realized.",
      bullets: [
        "Curiosity.",
        "Creativity.",
        "Desire.",
        "Rest.",
        "Healing.",
        "Possibility.",
        "A deeper relationship with yourself.",
        "A life that doesn't need an audience to be meaningful.",
        "And a person you may discover you genuinely enjoy spending time with.",
      ],
    },
  ],
  reflection_prompts: [
    "When I'm alone, what am I most tempted to escape from?",
    "What becomes easier to hear when my life gets quiet?",
    "What have I discovered about myself during seasons of solitude?",
    "What would I do differently if I stopped treating alone time as time to fill?",
    "What is something I would love to experience even if no one came with me?",
  ],
  emphasized_prompt:
    "What if this isn't an empty space in my life, but fertile ground for who I'm becoming?",
  epilogue: [
    "You don't have to romanticize loneliness to appreciate solitude. You don't have to stop wanting love, friendship, intimacy, or community to become comfortable in your own company. You can desire connection without making your life dependent upon its arrival.",
    "Being alone doesn't mean your life is empty. Sometimes the quietest chapters give you enough space to discover just how much is already there.",
    "And perhaps solitude isn't a place where life has left you. Perhaps it's one of the places where you finally get to meet yourself.",
  ],
};

const STOICISM = { ...STOIC_ARTICLE, slug: "stoicism", subtitle: "A guide to self-mastery and intentional living", minutes: 6, accent: "Compass", hero_image: "https://images.unsplash.com/photo-1519638399535-1b036603ac77?w=1800&q=80&auto=format&fit=crop" };

const EMOTIONAL_INTELLIGENCE = {
  slug: "emotional-intelligence",
  title: "Mastering Your Emotional Intelligence",
  subtitle: "The quiet skill that lets you move through the world with clarity, calm, and intention",
  minutes: 6,
  accent: "Brain",
  hero_image:
    "https://images.unsplash.com/photo-1517048676732-d65bc937f952?w=1800&q=80&auto=format&fit=crop",
  intro:
    "Emotional intelligence is the art of understanding your inner world so you can move through the outer world with clarity, calm, and intention. It's the ability to notice what you feel, name it, and choose your response instead of being controlled by it. Most people live on emotional autopilot — reacting from old patterns, old wounds, old habits. Emotional intelligence breaks that cycle. It teaches you to pause. To breathe. To choose. To act from alignment instead of impulse. And that's where your greatest self begins.",
  sections: [
    {
      heading: "Self-Awareness: Seeing Yourself Clearly",
      body:
        "Self-awareness is the foundation of emotional intelligence. It's the moment you recognize: I'm triggered. I'm overwhelmed. I'm shutting down. I'm acting from fear instead of truth. When you can see your emotional state clearly, you gain power over it. Self-awareness doesn't judge — it simply observes. It says: this is what I'm feeling, and I get to choose what happens next. That choice is freedom.",
    },
    {
      heading: "Self-Regulation: Mastering Your Inner World",
      body:
        "Self-regulation is not suppressing emotions. It's guiding them. It's the ability to stay grounded when life pulls at you. To breathe instead of react. To respond instead of explode. To pause instead of spiral.",
      bullets: [
        "Calm — the settled body from which clear thought becomes possible.",
        "Confidence — trusting that you can meet whatever the moment brings.",
        "Emotional stability — a steady inner weather, not a perfect one.",
        "Inner strength — the quiet muscle that lets you carry hard things without breaking.",
      ],
      closing: "This is how you become someone you trust.",
    },
    {
      heading: "Emotional Ownership: Taking Responsibility for Your Energy",
      body:
        "Emotional intelligence teaches you a hard, freeing truth: your emotions are yours, your reactions are yours, your energy is yours. You stop blaming others for how you feel. You stop giving your power away. You stop letting external chaos dictate your internal state. Ownership is the moment you say: I am responsible for the energy I bring into my life. This is where self-mastery begins.",
    },
    {
      heading: "Empathy: Understanding Without Absorbing",
      body:
        "Empathy is the ability to understand someone's experience without losing yourself in it.",
      bullets: [
        "Listening without fixing.",
        "Caring without carrying.",
        "Supporting without sacrificing your peace.",
      ],
      closing:
        "Empathy strengthens relationships, communication, and connection — but emotional intelligence teaches you to hold empathy with boundaries. You can be open without being overwhelmed. You can be kind without abandoning yourself.",
    },
    {
      heading: "Intentional Action: Choosing Your Response",
      body:
        "The greatest version of you is not built through perfection. It's built through intention. Emotional intelligence gives you the space to choose:",
      bullets: [
        "The aligned response instead of the automatic one.",
        "The grounded reaction instead of the impulsive one.",
        "The calm decision instead of the panicked one.",
        "The confident action instead of the fearful one.",
      ],
      closing:
        "It teaches you to act from your values, not your impulses. This is how you build a life that feels like yours.",
    },
    {
      heading: "Identity Shift: Becoming Your Highest Self",
      body:
        "When you practice emotional intelligence consistently, something profound happens: your identity shifts. You become someone who responds with clarity, moves with intention, protects their peace, honors their energy, chooses alignment, and builds confidence through consistency. You stop living from old patterns. You start living from your future self.",
      closing:
        "This is how emotional intelligence transforms you — not through force, not through pressure, but through awareness, regulation, ownership, empathy, and intention.",
    },
    {
      heading: "The takeaway",
      body:
        "Emotional intelligence is the foundation of self-mastery, confidence, alignment, and becoming the greatest version of yourself. When you learn to guide your inner world, your outer world transforms.",
    },
  ],
  reflection_prompts: [
    "Which of the six practices — awareness, regulation, ownership, empathy, intention, identity — feels most alive in you right now? Which feels most in need of tending?",
    "Where in your life are you still living on emotional autopilot? What would a single grounded pause change?",
    "If you spoke to yourself with the same steadiness you offer other people, what would shift by the end of this week?",
  ],
};

const MOVEMENT = {
  slug: "movement",
  title: "The Power of Movement",
  subtitle: "Consistent, intentional motion for a clear mind and a steady body",
  minutes: 4,
  accent: "Activity",
  hero_image:
    "https://images.unsplash.com/photo-1506126613408-eca07ce68773?w=1800&q=80&auto=format&fit=crop",
  intro:
    "Movement is one of the most reliable ways to reset your body and sharpen your mind. When you move, your circulation increases, your breath deepens, and your nervous system shifts out of stress and into balance. It's a simple, physical way to get centered in yourself. Movement clears mental fog. It stabilizes mood. It improves focus. It helps your brain and heart work together instead of pulling in different directions. You don't need intense workouts or complicated routines. You just need consistent, intentional movement woven into your week. Movement keeps your body strong, your mind clear, and your emotional state steady. It's one of the most effective tools for long-term wellbeing.",
  sections: [
    {
      heading: "Weekly Movement Practices to Incorporate",
      body:
        "These are the three most effective forms of movement for grounding, coherence, and overall health — with simple time recommendations.",
    },
    {
      heading: "1. Walking (20–30 minutes, 3–5× per week)",
      body:
        "Walking regulates your nervous system, improves circulation, and supports brain–heart coherence. It's one of the easiest ways to get centered and present.",
      bullets: [
        "Reduces stress hormones.",
        "Boosts mood.",
        "Improves cognitive clarity.",
        "Supports longevity.",
      ],
    },
    {
      heading: "2. Stretching or Mobility Work (10–15 minutes, 3–4× per week)",
      body:
        "Gentle stretching releases tension stored in the body and improves physical awareness. It helps you reconnect with yourself and move more freely.",
      bullets: [
        "Reduces stiffness.",
        "Improves posture.",
        "Increases body awareness.",
        "Supports emotional regulation.",
      ],
    },
    {
      heading: "3. Strength or Resistance Training (20–30 minutes, 2–3× per week)",
      body:
        "Strength work stabilizes joints, improves metabolic health, and builds physical resilience. It also boosts confidence and mental clarity.",
      bullets: [
        "Increases muscle strength.",
        "Improves bone density.",
        "Enhances mood.",
        "Supports long-term health.",
      ],
    },
    {
      heading: "Optional Add-Ons (1–2× per week)",
      body:
        "These aren't required, but they amplify grounding and coherence:",
      bullets: [
        "Yoga (20–40 minutes) — combines breath, movement, and presence.",
        "Breath-paired movement (5–10 minutes) — slow, intentional motions synced with breath.",
        "Outdoor movement — sunlight and fresh air for improved mood and clarity.",
      ],
    },
    {
      heading: "Quiet Summary",
      body:
        "Movement keeps your body strong, your mind clear, and your emotional state balanced. A few simple practices each week can improve focus, reduce stress, and help you feel more centered in yourself. Consistency matters more than intensity — just move, regularly and intentionally.",
    },
  ],
  reflection_prompts: [
    "Which of the three core practices — walking, stretching, or strength — is quietly missing from your week?",
    "What is one 10-minute movement you could anchor to something you already do?",
    "When you've been consistent with movement in the past, what did it give you — and how quickly did you forget?",
  ],
};

const SLEEP = {
  slug: "sleep",
  title: "The Hours That Heal You",
  subtitle: "Protecting the sleep that quietly does the work",
  minutes: 4,
  accent: "Moon",
  hero_image:
    "https://images.unsplash.com/photo-1602934585418-f588bea4215c?w=1800&q=80&auto=format&fit=crop",
  intro:
    "More real recovery happens in the seven hours you sleep than in almost anything else you do all day. You cannot supplement your way out of a sleep debt, and every other longevity habit compounds when this one is honored.",
  sections: [
    {
      heading: "Why sleep does the heavy lifting",
      body:
        "Sleep is when memory is consolidated, cellular waste is cleared from the brain, hormones are rebalanced, and inflammation quietly drops. It is not a passive state; it is the most active repair process in your day. Skimping on it is not a badge — it is a slow tax.",
    },
    {
      heading: "The four levers that matter most",
      subsections: [
        {
          title: "Regularity",
          body:
            "A consistent bedtime and wake time is more powerful than a long night. Your body optimizes around the schedule you actually keep, not the one you meant to. Weekends included where you can.",
        },
        {
          title: "Light, on purpose",
          body:
            "Morning sunlight within an hour of waking, and dim warm light in the last hour before bed. This one habit trains cortisol to rise in the morning and melatonin to release at night — no supplement required.",
        },
        {
          title: "A cool, dark, quiet room",
          body:
            "18–20°C. Blackout or a soft eye mask. Silence or gentle white noise. The body sleeps deepest when it drops slightly in core temperature — and even small light leaks quietly disrupt melatonin.",
        },
        {
          title: "A 60-minute wind-down",
          body:
            "One hour before bed: no work, no bright screens, low light, slow tasks. Signals the nervous system that the day is truly over. Bed becomes the place for rest, not for scrolling.",
        },
      ],
      closing:
        "None of this is glamorous. All of it is transformative — quietly, and over years.",
    },
    {
      heading: "Small choices that protect the hours",
      bullets: [
        "Cut caffeine after noon. It has a six-to-eight-hour half-life, and a three o'clock coffee is still working at nine.",
        "Move in daylight. Morning light and gentle movement together improve the same night's sleep more than either alone.",
        "If you wake in the night, don't reach for the phone. Rest with eyes closed is still deeply restorative.",
        "Alcohol looks like a sedative and behaves like a stimulant. It costs you the deep hours you need most.",
      ],
    },
  ],
  reflection_prompts: [
    "What is one small change you could make tonight that would protect tomorrow?",
    "Which of the four levers is easiest to honor this week? Which is quietly hardest?",
    "What has your sleep given you when you have actually protected it?",
  ],
};

const SELF_COMPASSION = {
  slug: "self-compassion",
  title: "Your Inner Voice",
  subtitle: "The quiet practice of meeting yourself with understanding instead of judgment",
  minutes: 5,
  accent: "Heart",
  hero_image:
    "https://images.unsplash.com/photo-1544367567-0f2fcb009e0b?w=1800&q=80&auto=format&fit=crop",
  intro:
    "Self-compassion is the practice of treating yourself with the same understanding you offer others. It's the moment you stop attacking yourself for being human and start supporting yourself through the reality of your experience. Most people carry an inner voice that is sharper than anything the outside world could say. They judge their emotions, criticize their mistakes, and expect themselves to move through life without ever stumbling. Self-compassion interrupts that pattern. It says: I can acknowledge what I feel without punishing myself for feeling it.",
  sections: [
    {
      heading: "Why self-compassion matters",
      body:
        "Self-compassion doesn't remove difficulty. It removes the unnecessary suffering created by self-blame, shame, and harshness. It's the ability to say:",
      bullets: [
        "I'm learning.",
        "I'm growing.",
        "I'm allowed to be imperfect.",
        "I can support myself instead of tearing myself down.",
      ],
      closing:
        "When you practice self-compassion, your nervous system softens. Your inner dialogue becomes clearer. Your decisions become more grounded. You stop reacting from fear and start responding from stability. Self-compassion is not indulgence. It's emotional maturity. It's the foundation of resilience. Because when you stop treating yourself like an enemy, you finally have the energy to grow.",
    },
    {
      heading: "Ways to become more self-compassionate",
      body:
        "These practices are simple, practical, and effective — small shifts that create long-term change.",
    },
    {
      heading: "1. Speak to yourself the way you'd speak to someone you care about",
      body:
        "If you wouldn't say it to a friend, don't say it to yourself. Replace harsh self-talk with neutral or supportive language.",
    },
    {
      heading: "2. Allow yourself to feel without judgment",
      body:
        "Emotions aren't failures. They're signals. Let them exist without labeling them as \"too much,\" \"wrong,\" or \"weak.\"",
    },
    {
      heading: "3. Practice small acts of self-kindness daily",
      body: "This can be:",
      bullets: [
        "Taking a break.",
        "Drinking water.",
        "Stepping outside.",
        "Slowing your breath.",
        "Choosing rest without guilt.",
      ],
      closing: "Small kindnesses build internal safety.",
    },
    {
      heading: "4. Notice your inner critic — and soften it",
      body:
        "You don't have to silence it. Just recognize when it appears and gently shift your tone. Awareness alone reduces its power.",
    },
    {
      heading: "5. Accept that growth includes mistakes",
      body:
        "Mistakes are not evidence of inadequacy. They are evidence of effort. Self-compassion reframes them as part of the process, not proof of failure.",
    },
    {
      heading: "6. Treat your needs as valid",
      body:
        "Your need for rest, space, clarity, or support is not a burden. It's part of being human. Honoring your needs builds self-respect.",
    },
    {
      heading: "7. Practice self-forgiveness",
      body:
        "Let go of the idea that you should have known better. You knew what you knew then. You know more now. That's growth.",
    },
    {
      heading: "Quiet Summary",
      body:
        "Self-compassion is the practice of meeting yourself with understanding instead of judgment. It strengthens emotional stability, supports growth, and creates the inner safety needed to become your best self. With small, consistent acts of kindness toward yourself, you build a foundation of resilience, clarity, and confidence.",
    },
  ],
  reflection_prompts: [
    "What did you say to yourself today that you would never say to a friend?",
    "Where has self-criticism cost you more than it has ever gained you?",
    "Which of the seven practices could you offer yourself this week, without waiting until you've earned it?",
  ],
};

const MANIFESTATION = {
  slug: "science-of-manifestation",
  title: "The Science Behind Manifestation",
  subtitle: "Understanding what shapes your reality",
  minutes: 6,
  accent: "Atom",
  hero_image:
    "https://images.unsplash.com/photo-1451187580459-43490279c0fa?w=1800&q=80&auto=format&fit=crop",
  intro:
    "Manifestation is the process of aligning your mind, nervous system, and behavior with a desired outcome — and quantum physics adds a layer showing that reality itself is probabilistic, responsive to observation, and shaped by interaction. It's not magic. It's mechanics, psychology, and the nature of the universe woven together.",
  sections: [
    {
      heading: "The Brain Science",
      subsections: [
        {
          title: "Reticular Activating System (RAS)",
          body:
            "Your brain's filter system. When you focus on a desire, your RAS begins highlighting anything related to it — people, ideas, opportunities. This is why intention changes what you notice.",
        },
        {
          title: "Neuroplasticity",
          body:
            "Repetition strengthens neural pathways. Thinking \"I'm becoming this version of myself\" literally rewires your brain to behave like that version of you.",
        },
        {
          title: "Emotional Encoding",
          body:
            "Emotion amplifies memory and action. Visualization with feeling primes your body to act as if the desired reality is already unfolding.",
        },
        {
          title: "Behavioral Micro-Shifts",
          body:
            "Manifestation changes your tone, choices, and timing. Those tiny shifts compound into real-world outcomes.",
        },
      ],
      closing:
        "This is the psychological foundation — but manifestation becomes far more interesting when you add quantum physics.",
    },
    {
      heading: "Quantum Physics: Why Your Focus Matters",
      body:
        "Quantum physics describes reality at the smallest scales — electrons, photons, atoms — and it behaves nothing like everyday matter. Three features are especially relevant to manifestation:",
      subsections: [
        {
          title: "Superposition",
          body:
            "A quantum system exists in multiple possible states until it's measured. Reality at its base is not fixed — it's probabilistic.",
        },
        {
          title: "Wave–Particle Duality",
          body:
            "Particles behave as both waves and solid objects depending on how they're observed. Your interaction changes the behavior of the system.",
        },
        {
          title: "Entanglement",
          body:
            "Two particles can become linked so that a change in one instantly affects the other, even across vast distances. Systems can be connected in ways that defy classical logic.",
        },
      ],
      closing:
        "These aren't metaphors — they're experimentally verified behaviors of the universe.",
    },
    {
      heading: "The Measurement Problem",
      body:
        "Quantum physics shows that observation is not passive. To measure something, you must interact with it — and that interaction changes the system. This is the scientific backbone of the idea that attention influences outcomes. Not because your thoughts magically control particles — but because:",
      bullets: [
        "Observation collapses possibilities into a single outcome.",
        "Your focus determines what you observe.",
        "What you observe determines how you act.",
        "How you act determines the reality you experience.",
      ],
      closing:
        "Manifestation is the human-scale expression of quantum principles.",
    },
    {
      heading: "The Bridge Between Mind and Quantum Reality",
      body:
        "Quantum physics does not say \"thoughts directly change particles.\" But it does say:",
      bullets: [
        "Reality is not fixed until interaction occurs.",
        "Observation changes outcomes.",
        "Systems can be connected in non-local ways.",
        "Consciousness may be linked to quantum information processing.",
      ],
      closing:
        "When you combine this with neuroscience: your thoughts shape your perception, your perception shapes your choices, and your choices shape your reality. Quantum physics shows the universe is built on probability, interaction, and responsiveness. Neuroscience shows your mind is built on focus, expectation, and patterning. Manifestation is where those two meet.",
    },
    {
      heading: "The Energetic Layer",
      body: "Humans are quantum observers:",
      bullets: [
        "Your internal state changes how you interact with the world.",
        "People respond differently to confidence, clarity, and grounded expectation.",
        "Your \"frequency\" — your emotional tone — influences your relational field.",
      ],
      closing:
        "This is not mystical; it's emergent behavior from psychology plus physics.",
    },
    {
      heading: "The Full Understanding",
      body: "Manifestation works because:",
      bullets: [
        "Your brain rewires itself toward your desired reality.",
        "Your nervous system primes your body to act in alignment.",
        "Your behavior shifts in thousands of subtle ways.",
        "Your observation collapses possibilities into chosen outcomes.",
        "Your energy influences your relational environment.",
      ],
      closing:
        "Manifestation is not wishing. It's participating with reality at every level — mental, emotional, behavioral, and quantum.",
    },
    {
      heading: "Quiet Summary",
      body:
        "Manifestation, very simply put, is holding a clear desire and consistently aligning your thoughts, emotions, and small daily actions with it. When your inner state matches the reality you want, you naturally move toward it — and it moves toward you.",
    },
  ],
  reflection_prompts: [
    "What outcome are you quietly holding right now — and does your daily behavior match the version of you that already lives there?",
    "Where has your focus been collapsing possibilities you didn't actually want?",
    "If observation shapes reality, what is one thing you could pay closer, steadier attention to this week?",
  ],
};

const SELF_AWARENESS = {
  slug: "becoming-self-aware",
  title: "Becoming Self Aware",
  subtitle: "The foundation of inner peace",
  minutes: 5,
  accent: "Eye",
  hero_image:
    "https://images.unsplash.com/photo-1494548162494-384bba4ab999?w=1800&q=80&auto=format&fit=crop",
  intro:
    "Self-awareness is the moment you turn inward and meet yourself with honesty, gentleness, and clarity. It's the practice of noticing what's happening inside you — your thoughts, your emotions, your reactions — without judgment and without rushing to fix anything. It's the quiet skill of seeing yourself clearly. Most people move through life reacting from old patterns, old wounds, old habits. Self-awareness interrupts that cycle. It gives you space. It gives you breath. It gives you choice. It's the difference between living on autopilot and living with intention.",
  sections: [
    {
      heading: "Self-Awareness Begins With Noticing",
      body:
        "It starts small. A tightening in your chest. A shift in your breath. A thought that pulls you out of the moment. A reaction that feels bigger than the situation. A feeling that rises before you understand why. Self-awareness is simply the act of noticing these things — not fixing, not analyzing, not judging. Just noticing.",
      closing:
        "The moment you notice, you are no longer controlled by the emotion — you are observing it. And observation creates freedom.",
    },
    {
      heading: "Self-Awareness Creates Emotional Space",
      body:
        "When you become aware of what you feel, you create space between the emotion and the reaction. In that space, you can choose:",
      bullets: [
        "Calm instead of chaos.",
        "Clarity instead of confusion.",
        "Intention instead of impulse.",
        "Alignment instead of old patterns.",
      ],
      closing:
        "Self-awareness doesn't make life easier. It makes you steadier. It gives you the ability to respond from your highest self instead of your wounded self.",
    },
    {
      heading: "Self-Awareness Strengthens Identity",
      body:
        "The more you understand your inner world, the more grounded you become in who you are. You begin to see:",
      bullets: [
        "What drains you.",
        "What nourishes you.",
        "What triggers you.",
        "What supports you.",
        "What aligns with you.",
        "What pulls you out of yourself.",
      ],
      closing:
        "You stop abandoning yourself. You stop betraying your needs. You stop shrinking to fit places you've outgrown. Self-awareness becomes a form of self-respect.",
    },
    {
      heading: "Self-Awareness Is the Gateway to Self-Mastery",
      body:
        "When you can see yourself clearly, you can guide yourself wisely. You can regulate your emotions. You can shift your energy. You can choose your responses. You can act from intention. You can build confidence through consistency. You can become someone you trust.",
      closing:
        "Self-awareness is not the end of the journey — it's the beginning of becoming your greatest self. It's the foundation of emotional intelligence, inner peace, and aligned living.",
    },
    {
      heading: "Quiet Summary",
      body:
        "Self-awareness is the gentle practice of noticing your inner world with honesty and compassion. It creates emotional space, strengthens identity, and gives you the power to choose your responses with intention. When you learn to see yourself clearly, you begin to live from your highest self — grounded, present, and aligned.",
    },
  ],
  reflection_prompts: [
    "What did you notice in yourself today that you'd usually rush past? What was it trying to tell you?",
    "Where in your life are you still reacting from an old pattern? What would one moment of pause change?",
    "If you spent this week simply noticing — no fixing, no judging — what do you think you would learn about yourself?",
  ],
};

export const QUIET_READS = [LOVE_YOU_BECOME, SOLITUDE, STOICISM, MANIFESTATION, EMOTIONAL_INTELLIGENCE, SELF_AWARENESS, MOVEMENT, SLEEP, SELF_COMPASSION];

export function findQuietRead(slug) {
  return QUIET_READS.find((a) => a.slug === slug) || null;
}
